import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormActionBar, Panel, PanelSection, WindowContent, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useWindowTitle } from '@/shared/windowing';
import { getGetVenuaPlannerProductQueryKey } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { DestinationSelectorField } from '@/modules/products/venua-planner/components/DestinationSelector';
import { FORM_LABEL_WIDTH } from '@/modules/products/venua-planner/formLayout';
import { useProductDetailForm } from './useProductDetailForm';
import type { ProductDetailFormValues } from './productDetailFormSchema';

export interface ProductDetailViewProps {
  layerId: string;
  productId: string;
  initialValues: ProductDetailFormValues;
}

/**
 * Orchestrátor detailu produktu nad TanStack Form. Panely jsou přes celou šířku okna, obsah je
 * omezený na čitelnou šířku a zarovnaný vlevo. Ukládání (název + destinace) přes sdílený
 * `FormActionBar` v patičce.
 */
export function ProductDetailView({ layerId, productId, initialValues }: ProductDetailViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  // Dynamický název okna (taskbar / historie / hlavička) = název produktu.
  useWindowTitle(initialValues.name);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useProductDetailForm({
    productId,
    initialValues,
    onSaved: (id) => {
      // Detail (Orval klíč) + strom (seznamová vrstva modulu, kde se mění název uzlu produktu).
      // Viz GUIDELINES „Detail stránky — čerstvost dat".
      void queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerProductQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('productDetail.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('productDetail.toast.errorTitle'),
        description: t('productDetail.toast.saveError'),
      });
    },
  });

  return (
    <WindowContent
      as="form"
      width="full"
      data-testid="product-detail"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={closeForm}
              onSave={submit}
              saving={isSubmitting}
              disabled={!canSubmit}
            />
          )}
        </form.Subscribe>
      }
    >
      <Panel variant="filled" title={t('productDetail.name.sectionLabel')}>
        <div className="w-full max-w-220">
          <PanelSection>
            <form.Field name="name">
              {(field) => (
                <TextField
                  field={field}
                  label={t('productDetail.name.label')}
                  required
                  layout="inline"
                  labelWidth={FORM_LABEL_WIDTH}
                  placeholder={t('productDetail.name.placeholder')}
                  data-testid="product-detail-name-input"
                />
              )}
            </form.Field>

            <form.Field name="destinations">
              {(field) => (
                <DestinationSelectorField
                  field={field}
                  mode="multi"
                  label={t('productDetail.destinations.label')}
                  layout="inline"
                  labelWidth={FORM_LABEL_WIDTH}
                  placeholder={t('productDetail.destinations.placeholder')}
                  testId="product-detail-destinations"
                />
              )}
            </form.Field>
          </PanelSection>
        </div>
      </Panel>
    </WindowContent>
  );
}
